import React, { useCallback, useMemo } from "react";
import { Stack, Divider } from "@mui/material";
import {
  User,
  Organisation,
  CalendarSlot,
  Meeting,
  PatientSummary,
} from "../../../types";
import { getMeetingStatus } from "../../../types-helpers";
import { useGetMeeting } from "../../../store";
import AttendeeList from "../../attendees/attendee-list";
import { getAttendeeStatus } from "../../attendees/utils";
import PatientChip from "../../chips/patient-chip";
import MeetingStatusTag from "../meeting-status-tag";
import MeetingTimeProgress from "./meeting-time-progress";

interface AgendaMeetingInfoProps {
  event: CalendarSlot;
  handleSelectMeeting: (id: string) => void;
  teams: Organisation[];
  users: User[];
  patients: PatientSummary[];
}

const AgendaMeetingInfo: React.FC<AgendaMeetingInfoProps> = ({
  event,
  handleSelectMeeting,
  teams,
  users,
  patients,
}) => {
  const meeting = {
    ...event,
    organisationIds: event.organisationIds || [],
  };

  const patient = patients.find((item) => item.patientId == meeting.patientId);
  const patientLabel = patient?.displayId || "Unknown";

  const { data: updatedMeeting } = useGetMeeting(meeting.meetingId); // TODO gets every meeting, but don't want like this, want as part of summary in single get for calendar, causing too many calls, and delayed ui updating... 
  const finalMeeting: Meeting = updatedMeeting || (meeting as Meeting);

  // Memoize the meeting status check
  const { canJoin, status, message } = useMemo(() => {
    return getMeetingStatus(finalMeeting);
  }, [finalMeeting]);

  // Create stable callback for meeting info button
  const handleMeetingInfoClick = useCallback(() => {
    handleSelectMeeting(meeting.meetingId);
  }, [meeting.meetingId, handleSelectMeeting]);

  const selectedUsers = useMemo(() => {
    if (!users) {
      return [];
    }

    return (finalMeeting.attendeeIds || [])
      .map((userId) => users.find((user) => user.userId === userId))
      .filter((user): user is User => !!user)
      .map((user) => ({
        userId: user.userId,
        label: user.fullName || user.email || "Unknown user",
        status: getAttendeeStatus(
          status,
          finalMeeting.joinedInfo?.find(
            (joinInfo) => joinInfo.userId === user.userId,
          ),
        ),
        teams: (user.organisationIds || [])
          .map((teamId) =>
            teams.find(
              (team) => team.organisationId === teamId,
            ),
          )
          .filter(
            (team): team is Organisation => !!team,
          )
          .map((team) => ({
            teamId: team.organisationId,
            name: team.name,
            color: team.color,
          })),
      }));
  }, [
    finalMeeting.attendeeIds,
    finalMeeting.joinedInfo,
    teams,
    status,
    users,
  ]);

  const selectedTeams = useMemo(() => {
    if (!teams) {
      return [];
    }

    return (finalMeeting.organisationIds || [])
      .map((teamId) =>
        teams.find(
          (team) => team.organisationId === teamId,
        ),
      )
      .filter((team): team is Organisation => !!team)
      .map((team) => ({
        teamId: team.organisationId,
        name: team.name,
        color: team.color,
      }));
  }, [finalMeeting.organisationIds, teams]);

  return (
    <Stack
      direction={"row"}
      onClick={handleMeetingInfoClick}
      sx={{
        pr: 2,
        pl: 1,
        py: 1,
        backgroundColor: "#EEE",
        borderRadius: 3,
        border: "1px solid transparent",
        cursor: "pointer",
        transition: "background-color 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "#E4E4E4",
        },
      }}
    >
      <Stack
        spacing={1}
        direction={"row"}
        justifyContent={"space-between"}
        alignContent={"center"}
        width={"100%"}
      >
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"stretch"}
          justifyItems={"center"}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MeetingTimeProgress
              startTime={meeting.startTime}
              endTime={meeting.endTime}
            />
            <PatientChip label={patientLabel} size="medium" />
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Stack justifyContent="center">
            <AttendeeList
              selectedUsers={selectedUsers}
              selectedInviteEmails={[]}
              selectedTeams={selectedTeams}
              editable={false}
            />
          </Stack>
        </Stack>
        <Stack direction={"row"} spacing={3} alignItems={"center"}>
          <MeetingStatusTag
            canJoin={canJoin}
            status={status}
            message={message}
            meeting={finalMeeting}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgendaMeetingInfo;
